import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Th, Td } from "@/components/common";
import { Truck, Loader2, CheckCircle2, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";

const csvCell = (v) => {
  const s = v == null ? "" : String(v);
  return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const DhlImport = () => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const run = async (apply) => {
    if (!file) { toast.error("Bitte zuerst das DHL-PDF auswählen."); return; }
    setBusy(true); setRes(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/finance/dhl-import?apply=${apply}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setRes(r.data);
      if (apply) toast.success(`${r.data.booked} Sendungen gebucht`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const downloadUnmatchedCsv = () => {
    const rows = [
      ...(res?.preview?.unmatched || []).map((r) => ({ ...r, kind: "nicht zugeordnet", orders: "" })),
      ...(res?.preview?.ambiguous || []).map((r) => ({ ...r, kind: "mehrdeutig", orders: (r.order_numbers || []).join(" | ") })),
    ];
    if (!rows.length) { toast.error("Keine offenen Sendungen zum Export."); return; }
    const header = ["Empfaenger", "Betrag_EUR", "Sendungsnummer", "Status", "Auftragsnummern"];
    const lines = rows.map((r) => [r.recipient, Number(r.cost_net).toFixed(2), r.tracking, r.kind, r.orders].map(csvCell).join(";"));
    const csv = "\uFEFF" + [header.join(";"), ...lines].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dhl-nicht-zugeordnet-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-md border border-border bg-card p-4 space-y-4" data-testid="dhl-import-card">
      <div className="flex items-center gap-2 font-heading font-semibold"><Truck className="h-5 w-5" /> DHL-Versandkosten-Import (einmalig)</div>
      <p className="text-sm text-muted-foreground">PDF der DHL-Online-Frankierung hochladen. Zuordnung erfolgt über den Empfängernamen, Buchung mit 0 % USt (final, Listenpreis). Erst Vorschau, dann Übernehmen. Erneuter Upload bucht nichts doppelt.</p>
      <div className="flex flex-wrap items-center gap-3">
        <input type="file" accept="application/pdf" data-testid="dhl-file-input"
          onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
        <Button variant="outline" size="sm" disabled={busy || !file} onClick={() => run(false)} data-testid="dhl-preview-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Vorschau
        </Button>
        <Button size="sm" disabled={busy || !file || !res} onClick={() => run(true)} data-testid="dhl-apply-btn">Übernehmen</Button>
      </div>
      {res && (
        <div className="space-y-3" data-testid="dhl-import-result">
          <div className="flex flex-wrap gap-4 text-sm">
            <span className="flex items-center gap-1"><CheckCircle2 className="h-4 w-4 text-primary" /> Erkannt: <b>{res.parsed}</b></span>
            <span>Zuordenbar: <b>{res.matched}</b></span>
            <span>Nicht zugeordnet: <b>{res.unmatched}</b></span>
            <span>Mehrdeutig: <b>{res.ambiguous}</b></span>
            {res.apply && <span className="text-primary">Gebucht: <b>{res.booked}</b> · bereits gebucht: {res.skipped_already_booked}</span>}
          </div>
          {res.ambiguous > 0 && <div className="flex items-center gap-1 text-xs text-chart-3"><AlertTriangle className="h-4 w-4" /> Mehrdeutige Empfänger werden nicht automatisch gebucht.</div>}
          {(res.unmatched > 0 || res.ambiguous > 0) && (
            <Button variant="outline" size="sm" onClick={downloadUnmatchedCsv} data-testid="dhl-export-unmatched-btn">
              <Download className="h-4 w-4 mr-1.5" /> Nicht zugeordnete als CSV ({res.unmatched + res.ambiguous})
            </Button>
          )}
          <div className="overflow-x-auto rounded border border-border max-h-80">
            <table className="w-full text-sm"><thead className="bg-muted/50"><tr><Th>Empfänger</Th><Th>Betrag</Th><Th>Sendungsnr.</Th><Th>Auftrag</Th><Th>Status</Th></tr></thead>
              <tbody>
                {res.preview.matched.map((r) => (<tr key={r.tracking}><Td>{r.recipient}</Td><Td>{r.cost_net.toFixed(2)} €</Td><Td className="text-xs">{r.tracking}</Td><Td>{r.order_number}</Td><Td className="text-xs">{r.status}</Td></tr>))}
                {res.preview.unmatched.map((r) => (<tr key={r.tracking} className="text-muted-foreground"><Td>{r.recipient}</Td><Td>{r.cost_net.toFixed(2)} €</Td><Td className="text-xs">{r.tracking}</Td><Td>—</Td><Td className="text-xs">nicht zugeordnet</Td></tr>))}
                {res.preview.ambiguous.map((r) => (<tr key={r.tracking} className="text-chart-3"><Td>{r.recipient}</Td><Td>{r.cost_net.toFixed(2)} €</Td><Td className="text-xs">{r.tracking}</Td><Td>{(r.order_numbers || []).join(", ")}</Td><Td className="text-xs">mehrdeutig</Td></tr>))}
              </tbody></table>
          </div>
        </div>
      )}
    </div>
  );
};

export default DhlImport;
